import { useMemo } from "react";
import { Row, Col } from "react-bootstrap";
import { AreaChart, Area, XAxis, Tooltip, ResponsiveContainer } from "recharts";
import { BoxArrowUpRight } from "react-bootstrap-icons";
import { BaseWidget, WidgetIconButton } from "../common";
import { DateRangeSelect } from "../common/DateRangeSelect";
import { buildContractsQuery } from "../../hooks/buildContractsQuery";
import "../../styles/TrendWidget.css";
import { formatNumber, getRangeDates } from "../../constants";

export function TrendWidget({
  data,
  range,
  onRangeChange,
  isLoading,
  error,
  onNavigate,
  onDownload,
}) {
  // Aggregate daily points into monthly buckets and find the peak
  const { monthlyPoints, peakLabel } = useMemo(() => {
    if (!data?.points?.length) {
      return { monthlyPoints: [], peakLabel: "" };
    }

    const groups = {};
    data.points.forEach((p) => {
      const d = new Date(p.date);
      if (isNaN(d.getTime())) return;
      const key = `${d.getFullYear()}-${String(d.getMonth()).padStart(2, "0")}`;
      if (!groups[key]) {
        groups[key] = {
          sortKey: key,
          month: d.toLocaleDateString("en-US", { month: "short" }),
          contracts: 0,
        };
      }
      groups[key].contracts += p.count || 0;
    });

    const points = Object.values(groups).sort((a, b) =>
      a.sortKey.localeCompare(b.sortKey),
    );

    const peak = points.reduce(
      (best, p) => (p.contracts > (best?.contracts || 0) ? p : best),
      null,
    );

    return {
      monthlyPoints: points,
      peakLabel: peak ? `${peak.month} (${formatNumber(peak.contracts)})` : "",
    };
  }, [data]);

  const total = data?.total || 0;

  // Carry the date range through to /contracts when navigating
  const resolvedDates = useMemo(() => getRangeDates(range), [range]);

  const rangeLabel = useMemo(() => {
    const { startDate, endDate } = resolvedDates || {};
    if (!startDate || !endDate) return "";
    const fmt = (v) => {
      const [y, m, d] = String(v).slice(0, 10).split("-").map(Number);
      return new Date(y, m - 1, d).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    };
    return `${fmt(startDate)} – ${fmt(endDate)}`;
  }, [resolvedDates]);

  const handleNavigate = () => {
    onNavigate(
      "/contracts?" +
        buildContractsQuery({
          status: "Accepted",
          acceptedStartDate: resolvedDates.startDate,
          acceptedEndDate: resolvedDates.endDate,
        }),
    );
  };

  return (
    <BaseWidget
      title="New Contracts"
      isLoading={isLoading}
      error={error}
      headerRight={
        <>
          <DateRangeSelect value={range} onChange={onRangeChange} />
          <WidgetIconButton
            icon={<BoxArrowUpRight />}
            title="View all"
            onClick={handleNavigate}
          />
        </>
      }
    >
      {data && (
        <Row className="g-2 align-items-center cp-trend-widget__head justify-content-between">
          <Col xs="auto">
            <span className="cp-trend-widget__total">
              {total.toLocaleString()}
            </span>
          </Col>
          <Col xs="auto" className="text-end">
            {rangeLabel && (
              <div className="cp-trend-widget__range">{rangeLabel}</div>
            )}
            {peakLabel && (
              <span className="cp-trend-widget__peak">Peak: {peakLabel}</span>
            )}
          </Col>
        </Row>
      )}

      <div className="cp-trend-widget__chart">
        {monthlyPoints.length > 0 && (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart
              data={monthlyPoints}
              margin={{ top: 5, right: 4, left: 4, bottom: 0 }}
            >
              <defs>
                <linearGradient id="cpGoldFade" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#c9941e" stopOpacity={0.25} />
                  <stop offset="100%" stopColor="#c9941e" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis
                dataKey="month"
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 10.5, fill: "#9ca3af" }}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: "#fff",
                  border: "1px solid #e5e7eb",
                  borderRadius: 8,
                  fontSize: 11,
                }}
                labelStyle={{ color: "#6b7280" }}
                formatter={(value) => [formatNumber(value), "Contracts"]}
              />
              <Area
                type="monotone"
                dataKey="contracts"
                stroke="#c9941e"
                strokeWidth={2.25}
                fill="url(#cpGoldFade)"
                dot={{ r: 2.5, fill: "#c9941e", strokeWidth: 0 }}
                activeDot={{ r: 4 }}
                isAnimationActive={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>
    </BaseWidget>
  );
}
